'use client';
import React from 'react';
import Link from 'next/link';
import { ArrowRight, ShieldCheck, Zap, Clock } from 'lucide-react';

export default function HeroSection() {
  return (
    <section className="gradient-hero min-h-screen flex items-center relative overflow-hidden">
      {/* Background decorative circles */}
      <div className="absolute top-20 right-10 w-96 h-96 rounded-full bg-accent/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-10 w-72 h-72 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-white/3 blur-3xl pointer-events-none" />
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
        {/* Left content */}
        <div className="animate-slide-up">
          <div className="inline-flex items-center gap-2 bg-accent/15 border border-accent/30 text-accent text-xs font-600 px-3 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
            Trusted by 500,000+ Nigerians
          </div>

          <h1 className="text-4xl md:text-5xl xl:text-6xl font-800 text-white leading-tight text-balance mb-6">
            Your Trusted{' '}
            <span className="text-accent">Digital Payment</span>{' '}
            Partner
          </h1>

          <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-lg">
            Buy airtime, subscribe to data, pay electricity and cable TV bills, transfer funds, and manage digital payments instantly with <span className="text-white font-600">DakaPay</span>.
          </p>

          <div className="flex flex-wrap gap-4 mb-10">
            <Link
              href="/sign-up-login-screen"
              className="btn-primary flex items-center gap-2 px-7 py-3.5 rounded-xl text-base"
            >
              Get Started Free
              <ArrowRight size={18} />
            </Link>
            <a
              href="#"
              className="btn-outline-white flex items-center gap-2 px-7 py-3.5 rounded-xl text-base"
            >
              Download App
            </a>
          </div>

          <div className="flex flex-wrap gap-6">
            {[
              { icon: ShieldCheck, text: 'Bank-grade Security' },
              { icon: Zap, text: 'Instant Delivery' },
              { icon: Clock, text: '24/7 Support' },
            ]?.map((item) => (
              <div key={`trust-${item?.text}`} className="flex items-center gap-2 text-white/60 text-sm">
                <item.icon size={15} className="text-accent" />
                <span>{item?.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right — floating card mockup */}
        <div className="hidden lg:flex items-center justify-center">
          <div className="relative">
            {/* Main wallet card */}
            <div className="glass-card rounded-2xl p-6 w-80 shadow-card-hover">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-white/50 text-xs font-500 uppercase tracking-wider">Wallet Balance</p>
                  <p className="text-white text-3xl font-700 tabular-nums mt-1">₦247,850.00</p>
                </div>
                <div className="w-12 h-12 rounded-full gradient-green flex items-center justify-center">
                  <span className="text-white font-800 text-lg">₦</span>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {['Airtime', 'Data', 'Bills', 'Transfer']?.map((s) => (
                  <div key={`hero-svc-${s}`} className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white text-lg">
                      {s === 'Airtime' ? '📱' : s === 'Data' ? '📶' : s === 'Bills' ? '⚡' : '💸'}
                    </div>
                    <span className="text-white/60 text-[10px] font-500">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Floating success toast */}
            <div className="absolute -bottom-6 -right-6 glass-card rounded-xl px-4 py-3 flex items-center gap-3 w-60">
              <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                <ShieldCheck size={14} className="text-accent" />
              </div>
              <div>
                <p className="text-white text-xs font-600">Airtime Delivered!</p>
                <p className="text-white/50 text-[10px]">₦500 to 0803 *** 4521</p>
              </div>
            </div>

            {/* Floating network badge */}
            <div className="absolute -top-4 -left-4 glass-card rounded-xl px-3 py-2 flex items-center gap-2">
              <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
              <span className="text-white text-xs font-500">All Networks Active</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}